import { ButtonLink } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchResults } from '@/components/company/company-search';
import { Page, PageHeader } from '@/components/layout/page';
import { DevNote } from '@/components/ui/dev-note';
import { Alert, Container, Section } from '@/components/ui/primitives';
import { MIN_QUERY_LENGTH, useCompanySearch } from '@/hooks/use-company-search';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { formatCountry } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Page de recherche.
 *
 * Elle synchronise le terme recherché avec l'URL (`/recherche?q=…`) : la page
 * devient partageable, le bouton « précédent » du navigateur fonctionne, et
 * un rechargement retrouve les mêmes résultats. C'est la raison pour laquelle
 * elle gère son champ elle-même plutôt que de réutiliser `<CompanySearch>`.
 *
 * Le remplacement d'entrée d'historique (`replace: true`) évite d'empiler une
 * entrée par frappe : sans lui, revenir en arrière obligerait à traverser
 * toute la saisie caractère par caractère.
 */
export default function SearchPage() {
    const site = useSite();
    const { isAuthenticated } = useSession();
    const [searchParams, setSearchParams] = useSearchParams();

    const queryFromUrl = searchParams.get('q') ?? '';
    const [query, setQuery] = useState(queryFromUrl);

    const searchLocked = site.search_requires_auth && !isAuthenticated;
    const search = useCompanySearch(query, { enabled: !searchLocked });

    // L'URL suit le terme réellement envoyé à l'API, pas chaque frappe.
    useEffect(() => {
        if (search.submittedQuery && search.submittedQuery !== queryFromUrl) {
            setSearchParams({ q: search.submittedQuery }, { replace: true });
        }
    }, [search.submittedQuery, queryFromUrl, setSearchParams]);

    return (
        <Page title="Rechercher une entreprise">
            <PageHeader
                eyebrow="Recherche"
                title="Rechercher une entreprise"
                description={`Par dénomination ou par identifiant national. Le registre interrogé est celui de ${formatCountry(site.country)} : le pays est déduit du site, jamais transmis dans la requête.`}
            >
                <div className="border-line-strong bg-surface flex max-w-2xl items-center gap-2 rounded-xl border p-1.5 shadow-sm">
                    <Search className="text-ink-muted ml-2 size-5 shrink-0" aria-hidden />
                    <input
                        type="search"
                        autoFocus
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="BOULANGERIE DUPONT, 111222333…"
                        aria-label="Rechercher une entreprise"
                        className="text-ink placeholder:text-ink-muted/70 min-w-0 flex-1 bg-transparent py-2.5 outline-none"
                    />
                </div>
            </PageHeader>

            <Section className="py-10">
                <Container className="space-y-8">
                    {searchLocked ? (
                        <Alert tone="info" title="La recherche demande une connexion">
                            Ce site a activé l'option « recherche réservée aux comptes »
                            (<code className="font-mono text-xs">search_requires_auth</code> dans{' '}
                            <code className="font-mono text-xs">GET /site</code>).
                            <ButtonLink to={paths.login} size="sm" variant="secondary" className="mt-3">
                                Se connecter
                            </ButtonLink>
                        </Alert>
                    ) : query.trim().length < MIN_QUERY_LENGTH ? (
                        <p className="text-ink-muted text-sm">
                            Saisissez au moins {MIN_QUERY_LENGTH} caractères. En dessous, aucun appel n'est envoyé :
                            la réponse serait inexploitable et la requête, gaspillée.
                        </p>
                    ) : (
                        <SearchResults
                            results={search.results}
                            loading={search.loading}
                            empty={search.empty}
                            error={search.error}
                            query={search.submittedQuery}
                        />
                    )}

                    <DevNote title="Ce que fait cet écran" endpoints={['GET /companies/search?q=…']}>
                        <p>
                            La saisie est <strong>déboncée</strong> à 350 ms et n'appelle qu'à partir de{' '}
                            {MIN_QUERY_LENGTH} caractères : la recherche dispose d'un quota plus serré que le reste de
                            l'API, et une frappe non temporisée obtiendrait vite un{' '}
                            <code className="font-mono">429 rate_limited</code>.
                        </p>
                        <p>
                            Les réponses arrivées dans le désordre sont ignorées : seule celle de la dernière requête
                            émise est retenue. Ouvrez l'inspecteur pour compter les appels réellement partis.
                        </p>
                        <p>
                            Une recherche sans correspondance renvoie une <strong>liste vide</strong>, pas une erreur.
                        </p>
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}
